import { type SchedulingEmailAudience } from '../../../../models/Scheduling'
import { type TemplateLookup, type TemplateScope, type TemplateValue } from '../../../../models/TemplateLibrary'
import { findSchedulingEmail, SCHEDULING_EMAILS, type SchedulingEmailDefinition } from '../../../Scheduling/shared/schedulingEmailCatalog'
import { type DeleteModalCopy } from '../TemplateDeleteConfirmationModal'

export interface SchedulingEmail extends SchedulingEmailDefinition {
  defaultTemplateId: string
}

export interface SchedulingEmailGroup {
  email: SchedulingEmail
  templates: TemplateLookup[]
}

export interface SchedulingEmailList {
  emails: SchedulingEmail[]
  missingDefaultSlugs: string[]
}

export const readSchedulingEmails = (templates: TemplateLookup[]): SchedulingEmailList => {
  const read = SCHEDULING_EMAILS.map((email) => ({
    email,
    defaultTemplate: templates.find((template) => template.scope === 'system' && template.typeId === email.slug),
  }))
  return {
    emails: read.flatMap(({ email, defaultTemplate }) => (defaultTemplate != null ? [{ ...email, defaultTemplateId: defaultTemplate.id }] : [])),
    missingDefaultSlugs: read.filter(({ defaultTemplate }) => defaultTemplate == null).map(({ email }) => email.slug),
  }
}

const SCOPE_ORDER: TemplateScope[] = ['enterprise', 'account', 'user', 'system']

const byScope = (a: TemplateLookup, b: TemplateLookup) => SCOPE_ORDER.indexOf(a.scope) - SCOPE_ORDER.indexOf(b.scope)

export const matchesSearch = (template: TemplateLookup, search: string) => {
  const needle = search.trim().toLowerCase()
  return needle === '' || template.name.toLowerCase().includes(needle) || template.description.toLowerCase().includes(needle)
}

export const groupSchedulingEmailTemplates = (
  templates: TemplateLookup[],
  emails: SchedulingEmail[],
  audience: SchedulingEmailAudience,
  search: string,
): SchedulingEmailGroup[] =>
  emails
    .filter((email) => email.audience === audience)
    .map((email) => ({
      email,
      templates: templates.filter((template) => template.typeId === email.slug && matchesSearch(template, search)).sort(byScope),
    }))
    .filter((group) => group.templates.length > 0)

export const schedulingEmailDetails = (templateValue: TemplateLookup | TemplateValue): { label: string; value: string }[] => {
  const email = findSchedulingEmail(templateValue.typeId)
  return email
    ? [
        { label: 'Purpose', value: email.label },
        { label: 'Group', value: email.audience },
      ]
    : []
}

export const schedulingEmailDeleteCopy = (templateValue: TemplateLookup): DeleteModalCopy => {
  const emailLabel = findSchedulingEmail(templateValue.typeId)?.label
  return {
    title: 'Delete template?',
    notInUse: "This template isn't used anywhere. You can't undo this.",
    inUse: () =>
      emailLabel
        ? `This template is used for ${emailLabel} emails. Choose a replacement. You can't undo this.`
        : "This template is in use. Choose a replacement. You can't undo this.",
    replacementLabel: 'Use instead',
  }
}
